import { useEffect } from 'react';
import SEO from '@/components/SEO';
import { Sparkles, TrendingUp, Users, Zap, CheckCircle, Calendar, ArrowRight } from 'lucide-react';
import { site } from '@/data/content';

export default function News() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: 'ZuriHub Acquires AppNomu SalesQ for UGX 500,000,000',
    description: 'A transformative step forward in delivering comprehensive business communication and CRM solutions for African businesses.',
    datePublished: '2026-09-13',
    author: {
      '@type': 'Organization',
      name: 'ZuriHub',
    },
    publisher: {
      '@type': 'Organization',
      name: 'ZuriHub',
      logo: {
        '@type': 'ImageObject',
        url: `${site.baseUrl}/og-image.png`,
      },
    },
  };

  useEffect(() => {
    document.title = 'ZuriHub Acquires AppNomu SalesQ - ZuriHub';
  }, []);

  return (
    <>
      <SEO
        title="ZuriHub Acquires AppNomu SalesQ - ZuriHub"
        description="ZuriHub acquires AppNomu SalesQ for UGX 500,000,000, expanding CRM, SMS marketing, and business analytics capabilities. Transaction expected January 2027."
        path="/news"
        jsonLd={[jsonLd]}
      />

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 text-white py-24 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)'
            }}></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-full mb-8 shadow-lg">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-semibold tracking-wide uppercase">Breaking News</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight">
                ZuriHub Acquires <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200">AppNomu SalesQ</span>
              </h1>
              
              <div className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                UGX 500,000,000
              </div>
              
              <p className="text-xl md:text-2xl text-blue-100 mb-10 leading-relaxed max-w-3xl">
                A transformative step forward in delivering comprehensive business communication and CRM solutions for African businesses
              </p>
              
              <div className="flex flex-wrap items-center gap-6 text-sm text-blue-200">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <Calendar className="h-5 w-5" />
                  <span className="font-medium">Transaction: January 2027</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <Users className="h-5 w-5" />
                  <span className="font-medium">Bahati Asher Faith continues as CEO</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Article */}
            <div className="lg:col-span-2 space-y-8">
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  A Game-Changing Acquisition for African Business Technology
                </h2>
                
                <p className="text-gray-700 text-lg leading-relaxed">
                  We are thrilled to announce that ZuriHub has entered into a definitive agreement to acquire AppNomu SalesQ for UGX 500,000,000 (approximately USD 135,000), with the transaction expected to complete in January 2027. This strategic acquisition marks a significant milestone in our mission to provide African businesses with world-class communication and customer experience tools.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
                  What This Means for Our Customers
                </h3>
                
                <p className="text-gray-700 text-lg leading-relaxed">
                  Starting January 27, 2027, services such as SMS marketing, voice solutions, email campaigns, and advanced CRM capabilities will be fully integrated into the ZuriHub platform. While customers can continue accessing these services through the AppNomu SalesQ website during the transition period, all services will migrate to our unified platform, creating a seamless experience for businesses of all sizes.
                </p>

                <div className="bg-blue-50 border-l-4 border-blue-600 p-6 my-8 rounded-r-lg">
                  <p className="text-gray-800 font-medium mb-4">
                    AppNomu SalesQ has been a trusted partner since 2021, serving businesses globally:
                  </p>
                  <ul className="text-gray-700 space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span><strong>25,000 businesses</strong> across US, Africa, and Asia with powerful features powered by SalesQ AI</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span><strong>300+ startups</strong> supported with free voice calls, voice numbers, and free credits</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>CRM and customer relationship management</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>SMS marketing and bulk messaging solutions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Email campaign management</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>AI-powered business analytics</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Product management including inventory, sales tracking, and business funding tools</span>
                    </li>
                  </ul>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
                  Why This Acquisition Matters
                </h3>

                <p className="text-gray-700 text-lg leading-relaxed">
                  This acquisition represents more than just adding new features—it's about creating a comprehensive ecosystem that empowers African businesses to compete globally. By combining ZuriHub's AI-first customer experience platform with AppNomu SalesQ's proven CRM and marketing automation capabilities, we're building something truly transformative.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <TrendingUp className="h-8 w-8 text-blue-600 mb-4" />
                    <h4 className="font-bold text-gray-900 mb-2">Enhanced Marketing Tools</h4>
                    <p className="text-gray-600 text-sm">
                      Combine customer support with powerful SMS, email, and voice marketing campaigns—all in one platform.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <Zap className="h-8 w-8 text-purple-600 mb-4" />
                    <h4 className="font-bold text-gray-900 mb-2">AI-Powered Analytics</h4>
                    <p className="text-gray-600 text-sm">
                      Leverage advanced business intelligence to make data-driven decisions about sales, inventory, and customer engagement.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <Users className="h-8 w-8 text-green-600 mb-4" />
                    <h4 className="font-bold text-gray-900 mb-2">Unified Team Experience</h4>
                    <p className="text-gray-600 text-sm">
                      Your support, sales, and marketing teams will work from a single, cohesive platform instead of fragmented tools.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <CheckCircle className="h-8 w-8 text-orange-600 mb-4" />
                    <h4 className="font-bold text-gray-900 mb-2">Business Growth Tools</h4>
                    <p className="text-gray-600 text-sm">
                      Access product management, inventory tracking, and business funding solutions designed for African markets.
                    </p>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
                  Leadership Continuity
                </h3>

                <p className="text-gray-700 text-lg leading-relaxed">
                  We're pleased to confirm that Bahati Asher Faith will continue as CEO of ZuriHub, providing steady leadership through this exciting transition. The talented team at AppNomu SalesQ will join the ZuriHub family, bringing deep expertise in CRM, marketing automation, and business intelligence to strengthen our product vision.
                </p>

                <p className="text-gray-700 text-lg leading-relaxed">
                  This acquisition aligns perfectly with our vision of becoming the leading customer experience and business operations platform for African businesses. By integrating these powerful capabilities, we're not just adding features—we're creating a unified platform that helps businesses acquire customers, support them effectively, and grow sustainably.
                </p>

                <div className="bg-gradient-to-br from-slate-100 to-blue-50 p-8 rounded-xl my-10 border border-blue-200">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">About ZuriHub</h3>
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    Founded in 2026 by Bahati Asher Faith after spending over $50,000 on expensive support tools like Zendesk and Intercom, ZuriHub was built out of a real need. Our mission is to give every business, especially in Africa and emerging markets, access to enterprise-grade customer experience tools at a price that makes sense.
                  </p>
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    In just one year, ZuriHub has grown to serve 250 businesses. We've lived the problem we're solving, which is why we build features that actually matter. Our platform unifies live chat, tickets, knowledge base, omnichannel messaging, AI voice, and analytics in one place — replacing a mess of fragmented tools with a single, modern customer experience platform.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-blue-600 mb-2">250+</div>
                      <p className="text-gray-600">Businesses Served</p>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-purple-600 mb-2">10M+</div>
                      <p className="text-gray-600">Conversations Handled</p>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-green-600 mb-2">99.99%</div>
                      <p className="text-gray-600">Average Uptime</p>
                    </div>
                  </div>
                  <p className="text-gray-700 text-lg leading-relaxed mt-6">
                    Our core values are customer-first, ship fast, built on trust, and global from day one. We're committed to helping businesses acquire customers, support them effectively, and grow sustainably — regardless of their size or location.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-xl my-10">
                  <h3 className="text-2xl font-bold mb-4">What's Next for Our Customers</h3>
                  <p className="text-blue-100 mb-6">
                    Over the coming months, we'll be working closely with AppNomu SalesQ customers to ensure a smooth transition. You can expect:
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>Seamless migration of your existing campaigns, contacts, and data</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>Enhanced features and capabilities at no additional cost during transition</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>Dedicated support team to guide you through every step</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>Regular updates on integration progress and new feature releases</span>
                    </li>
                  </ul>
                  
                  <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
                    <h4 className="font-bold text-lg mb-3">For Existing AppNomu SalesQ Customers</h4>
                    <p className="text-blue-100 mb-3">
                      Customers currently using AppNomu SalesQ will be seamlessly redirected to the ZuriHub platform. To ensure a smooth transition and help you fully adapt to our comprehensive Customer Experience features, we're offering a <strong>6-month free offer</strong> on ZuriHub's advanced CX capabilities.
                    </p>
                    <p className="text-blue-100">
                      This includes access to our AI-powered support tools, omnichannel messaging, knowledge base, and advanced analytics — all designed to help you feel right at home from day one.
                    </p>
                  </div>
                </div>

                <p className="text-gray-700 text-lg leading-relaxed">
                  This is an incredibly exciting time for ZuriHub, our customers, and the broader African business technology ecosystem. We're committed to making this transition as smooth as possible while delivering even more value to our growing community of businesses across the continent.
                </p>

                <p className="text-gray-700 text-lg leading-relaxed font-medium">
                  Stay tuned for more updates as we work to bring these powerful new capabilities to the ZuriHub platform. The future of African business technology is being built here, and we're honored to have you on this journey with us.
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-4">Key Dates</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">January 2027</p>
                      <p className="text-sm text-gray-600">Transaction completion expected</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">January 27, 2027</p>
                      <p className="text-sm text-gray-600">Services migration to ZuriHub platform</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-4">Acquisition Value</h3>
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  UGX 500,000,000
                </div>
                <p className="text-sm text-gray-600">
                  Approximately USD 135,000
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-4">Learn More</h3>
                <div className="space-y-3">
                  <a href="/product" className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium">
                    Explore ZuriHub Features
                    <ArrowRight className="h-4 w-4" />
                  </a>
                  <a href="/pricing" className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium">
                    View Pricing Plans
                    <ArrowRight className="h-4 w-4" />
                  </a>
                  <a href="/book-a-demo" className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium">
                    Book a Demo
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
